import type { ListResponse, ListRequestOptions, FetchPageParams } from '~/types';

export function usePaginatedList<TItem>({
  path,
  dataRef,
  pageSize,
  loadingStatusRef
}: PaginatedListParams<ListResponse<TItem>>) {
  const { getListContent } = useContentApi();
  const loggingService = useLoggingService();

  function updateLoadingState(loadingType: LoadingType, state: boolean) {
    const loadingTypeArray = loadingStatusRef.value;
    const existing = loadingTypeArray.find((item) => item.type === loadingType);

    if (existing) {
      existing.state = state;
      return;
    }

    loadingTypeArray.push({ type: loadingType, state });
  }

  const fetchPage = async ({ page = 1, size = pageSize, filters = {}, loadingType }: FetchPageParams) => {
    const defaultFilters = {
      tags: '',
      recursive: false
    };

    const mergedFilters = { ...defaultFilters, ...filters };

    const requestOptions: ListRequestOptions = {
      url: useFrontendBase(path),
      page,
      pageSize: size,
      ...mergedFilters
    };

    try {
      updateLoadingState(loadingType, true);
      const result = await getListContent<ListResponse<TItem>>(requestOptions);
      updateLoadingState(loadingType, false);
      dataRef.value = result;

      return result;
    } catch (error) {
      loggingService.error(error);
      return null;
    }
  };

  const handlePageChange = ({ page, tags }: EmitPageChange, loadingType: LoadingType) => {
    fetchPage({
      page,
      filters: {
        tags
      },
      loadingType
    });
  };

  const handlePageSizeChange = ({ size }: EmitPageSizeChange, loadingType: LoadingType) =>
    fetchPage({ page: 1, size, loadingType });

  const handlePaginationUpdate = (payload: EmitPageChange | EmitPageSizeChange, loadingType: LoadingType) => {
    if ('page' in payload) {
      handlePageChange(payload, loadingType);
    } else if ('size' in payload) {
      handlePageSizeChange(payload, loadingType);
    }
  };

  return {
    fetchPage,
    handlePageChange,
    handlePageSizeChange,
    handlePaginationUpdate
  };
}
